/**
 * Bản đồ thông báo lỗi Zod mặc định bằng tiếng Việt.
 * Dùng chung cho backend (tRPC) và frontend (form) để không lộ thông báo tiếng Anh
 * như "Expected number, received string" hay "Number must be greater than or equal to 0".
 *
 * Chỉ áp dụng cho các lỗi KHÔNG có message tùy biến (các schema có message riêng vẫn giữ nguyên).
 * Không import zod để tránh lệch phiên bản giữa các package: sử dụng mã lỗi dạng chuỗi.
 */

import { fieldLabel } from './field-labels';

interface ViIssue {
  code: string;
  path?: (string | number)[];
  expected?: string;
  received?: string;
  minimum?: number | bigint;
  maximum?: number | bigint;
  inclusive?: boolean;
  exact?: boolean;
  type?: string;
  validation?: string | { includes?: string; startsWith?: string; endsWith?: string };
  options?: unknown[];
  keys?: string[];
  message?: string;
}

interface ViErrorMapCtx {
  defaultError: string;
  data: unknown;
}

/** "Khách hàng", "Số lượng (dòng 2)"; null khi trường chưa có nhãn (không lộ tên thuộc tính trong code) */
function fieldName(path?: (string | number)[]): string | null {
  if (!path || path.length === 0) return null;
  const lastKey = [...path].reverse().find((p) => typeof p === 'string') as string | undefined;
  const label = lastKey ? fieldLabel(lastKey) : undefined;
  if (!label) return null;
  const rowIndex = path.find((p) => typeof p === 'number') as number | undefined;
  return rowIndex !== undefined ? `${label} (dòng ${rowIndex + 1})` : label;
}

function fieldPrefix(path?: (string | number)[]): string {
  return fieldName(path) || 'Giá trị';
}

/** Trả về thông báo tiếng Việt cho từng loại lỗi Zod */
export const viZodErrorMap = (issue: ViIssue, _ctx?: ViErrorMapCtx): { message: string } => {
  const prefix = fieldPrefix(issue.path);

  switch (issue.code) {
    case 'invalid_type': {
      if (issue.received === 'undefined' || issue.received === 'null') {
        return { message: fieldName(issue.path) ? `${prefix} là bắt buộc` : 'Một ô bắt buộc chưa nhập' };
      }
      if (issue.expected === 'number') return { message: `${prefix} phải là số` };
      if (issue.expected === 'string') return { message: `${prefix} phải là chuỗi ký tự` };
      if (issue.expected === 'boolean') return { message: `${prefix} phải là giá trị đúng/sai` };
      if (issue.expected === 'integer') return { message: `${prefix} phải là số nguyên` };
      if (issue.expected === 'date') return { message: `${prefix} phải là ngày hợp lệ` };
      if (issue.expected === 'array') return { message: `${prefix} phải là danh sách` };
      return { message: `${prefix} không hợp lệ` };
    }
    case 'invalid_literal':
    case 'invalid_union':
    case 'invalid_union_discriminator':
    case 'invalid_arguments':
    case 'invalid_return_type':
    case 'invalid_intersection_types':
    case 'not_multiple_of':
      return { message: `${prefix} không hợp lệ` };
    case 'invalid_enum_value':
      return { message: `${prefix} không thuộc danh sách giá trị cho phép` };
    case 'unrecognized_keys':
      return { message: `Có trường dữ liệu không được hỗ trợ` };
    case 'invalid_date':
      return { message: `${prefix} phải là ngày hợp lệ` };
    case 'invalid_string': {
      if (issue.validation === 'email') return { message: 'Email không hợp lệ' };
      if (issue.validation === 'url') return { message: 'Đường dẫn (URL) không hợp lệ' };
      if (issue.validation === 'uuid') return { message: `${prefix} không đúng định dạng mã định danh` };
      if (issue.validation === 'regex') return { message: `${prefix} không đúng định dạng` };
      return { message: `${prefix} không đúng định dạng` };
    }
    case 'too_small': {
      const min = String(issue.minimum);
      if (issue.type === 'string') {
        return Number(issue.minimum) === 1
          ? { message: `${prefix} không được để trống` }
          : { message: `${prefix} phải có ít nhất ${min} ký tự` };
      }
      if (issue.type === 'array') return { message: `${prefix} phải có ít nhất ${min} phần tử` };
      if (issue.type === 'date') return { message: `${prefix} không được nhỏ hơn ngày cho phép` };
      return {
        message: issue.inclusive === false
          ? `${prefix} phải lớn hơn ${min}`
          : `${prefix} phải lớn hơn hoặc bằng ${min}`,
      };
    }
    case 'too_big': {
      const max = String(issue.maximum);
      if (issue.type === 'string') return { message: `${prefix} tối đa ${max} ký tự` };
      if (issue.type === 'array') return { message: `${prefix} tối đa ${max} phần tử` };
      if (issue.type === 'date') return { message: `${prefix} không được lớn hơn ngày cho phép` };
      return {
        message: issue.inclusive === false
          ? `${prefix} phải nhỏ hơn ${max}`
          : `${prefix} phải nhỏ hơn hoặc bằng ${max}`,
      };
    }
    case 'custom':
      return { message: issue.message || `${prefix} không hợp lệ` };
    default:
      return { message: `${prefix} không hợp lệ` };
  }
};
